import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { getWashVehicles, markVehicleWashed } from '../services/api';
import { toLocalISODate } from '../utils/dateUtils';
import { useApp } from '../context/AppContext';

const WashBadge = ({ status }) => {
  const cls = status === 'overdue' ? 'danger' : status === 'due' ? 'warn' : 'ok';
  const label = status === 'overdue' ? 'Overdue' : status === 'due' ? 'Due' : 'OK';
  return <span className={`badge ${cls}`}>{label}</span>;
};

const formatLastWashed = (value) => {
  if (!value) return 'Never';
  try {
    return format(new Date(value), 'dd MMM yyyy');
  } catch (_err) {
    return '—';
  }
};

const sortVehicles = (rows) => {
  return [...rows].sort((a, b) => {
    const aAssigned = a.washAssignment ? 1 : 0;
    const bAssigned = b.washAssignment ? 1 : 0;
    if (aAssigned !== bAssigned) return bAssigned - aAssigned;
    const aDue = a.wash?.due ? 1 : 0;
    const bDue = b.wash?.due ? 1 : 0;
    if (aDue !== bDue) return bDue - aDue;
    const aDays = a.wash?.daysUntilDue ?? 999;
    const bDays = b.wash?.daysUntilDue ?? 999;
    return aDays - bDays;
  });
};

export default function VehicleWash() {
  const { canManageAssignments } = useApp();
  const [date, setDate] = useState(() => toLocalISODate());
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tickingId, setTickingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getWashVehicles(date);
      const list = Array.isArray(data?.vehicles) ? data.vehicles : [];
      setVehicles(sortVehicles(list));
    } catch (err) {
      setError(err?.message || 'Failed to load wash list');
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkWashed = async (row) => {
    const id = row.vehicleId || row._id;
    if (!id) return;
    setTickingId(id);
    try {
      await markVehicleWashed(id, date);
      const by = row.washAssignment?.driverName;
      toast.success(
        by
          ? `${row.name || 'Vehicle'} marked washed — ${by} assignment cleared`
          : `${row.name || 'Vehicle'} marked washed`
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record wash');
    } finally {
      setTickingId(null);
    }
  };

  const assignedCount = vehicles.filter((v) => v.washAssignment?.driverId).length;

  return (
    <>
      <div className="page-header">
        <h2>Vehicle Wash</h2>
        <p>
          Each vehicle should be washed every 10 days (or its configured interval).
          Assigned washer names come from{' '}
          <Link to="/wash-assignments" style={{ color: 'var(--accent2)' }}>
            Daily Wash Assignments
          </Link>
          {' '}for the same date — you can still mark washed even if no washer is assigned yet.
        </p>
      </div>

      <div className="card">
        <div className="form-grid cols2" style={{ marginBottom: 16, maxWidth: 560 }}>
          <div className="form-group">
            <label>Wash date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ alignSelf: 'end' }}>
            <span className="text-muted text-sm">
              {assignedCount} of {vehicles.length} vehicles have a washer assigned for {date}
            </span>
            {canManageAssignments && assignedCount === 0 && !loading && (
              <div style={{ marginTop: 6 }}>
                <Link to="/wash-assignments" className="btn btn-sm btn-primary">
                  Assign washers for this date
                </Link>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : error ? (
          <div className="empty-state">
            <div className="icon">⚠</div>
            <p className="text-muted">{error}</p>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🚗</div>
            <p>No vehicles in the fleet yet.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Plate</th>
                  <th>Assigned washer</th>
                  <th>Last washed</th>
                  <th>Days since wash</th>
                  <th>Days until due</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((row) => {
                  const wash = row.wash || {};
                  const assignment = row.washAssignment;
                  const isDue = Boolean(wash.due);
                  const hasWasher = Boolean(assignment?.driverId);
                  const rowStyle = isDue
                    ? {
                        borderLeft: '4px solid var(--warn)',
                        background: 'rgba(245, 158, 11, 0.06)',
                      }
                    : undefined;
                  const id = row.vehicleId || row._id;
                  return (
                    <tr key={id} style={rowStyle}>
                      <td><strong>{row.name || '—'}</strong></td>
                      <td className="mono">{row.plate || '—'}</td>
                      <td>
                        {hasWasher ? (
                          <strong style={{ color: 'var(--accent2)' }}>{assignment.driverName}</strong>
                        ) : (
                          <span className="text-muted text-sm">Not assigned</span>
                        )}
                      </td>
                      <td className="mono text-sm">{formatLastWashed(row.lastWashAt)}</td>
                      <td className="mono">{wash.daysSinceWash ?? '—'}</td>
                      <td
                        className="mono"
                        style={{
                          color: isDue ? 'var(--danger)' : 'inherit',
                        }}
                      >
                        {wash.daysUntilDue != null
                          ? (wash.daysUntilDue < 0 ? `${wash.daysUntilDue} (overdue)` : wash.daysUntilDue)
                          : '—'}
                      </td>
                      <td><WashBadge status={wash.status || 'ok'} /></td>
                      <td>
                        <button
                          className={`btn btn-sm ${isDue ? 'btn-primary' : ''}`}
                          disabled={tickingId === id}
                          onClick={() => handleMarkWashed(row)}
                        >
                          {tickingId === id ? 'Saving…' : '✓ Mark washed'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
